/**
 * Ready to Prescribe (Treatment Plan spec v1.4, S-08 and S-09; canvas board 7).
 * Two tabs: "Ready to send", the accepted plans waiting to be prescribed, and
 * "Plan Questions", patients asking about the plan they were sent, longest wait
 * first, with the wait coloured from the clinic's own thresholds. The detail
 * shows what they asked, what they did on the plan, and a suggested answer the
 * provider edits. An answer that promises a duration or a result is refused
 * with the reason (PG-004), and the check runs live while the provider types.
 *
 * A third tab, "Sign" (S-23), shows prescribers the plans waiting for their
 * signature.
 *
 * Prescribing itself arrives with DoseSpot (phase 3); until then "Ready to
 * send" lists the plans and opens the patient.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { SignQueue, useSignQueue } from '../components/SignQueue';
import { Button, Card, EmptyState, ErrorState, Field, InfoHint, LoadingRows, PageHeader, inputClass } from '../components/ui';

interface Row {
  id: string;
  planId: string;
  patientId: string;
  patientName: string;
  initials: string;
  topicLabel: string;
  prescriber: string | null;
  question: string;
  yourWords: string | null;
  contactPref: string | null;
  askedAt: string;
  waitMinutes: number;
  wait: 'green' | 'amber' | 'red';
}
interface Detail extends Omit<Row, 'initials' | 'wait'> {
  planVersion: number;
  planLine: string;
  patientPhone: string | null;
  status: string;
  activity: string[];
  suggestedAnswer: string | null;
  answer: string | null;
}
interface ReadyCheck {
  id: string;
  ok: boolean;
  blocking: boolean;
  message: string;
}
interface ReadyPlan {
  planId: string;
  patientId: string;
  patientName: string;
  initials: string;
  items: string[];
  monthly: string;
  acceptedAt: string | null;
  healthCheckFlagged: boolean;
  /** S-08: Ready or Needs attention today; Waiting in DoseSpot and Prescribed arrive with DoseSpot. */
  status: 'ready' | 'needs_attention' | 'waiting_in_dosespot' | 'prescribed';
  checks: ReadyCheck[];
  dosespot: Array<{ id: string; message: string }>;
  version: number;
  signedBy: string | null;
  /** CN-006: the patient has not accepted a material consent update yet. */
  consentUpdatePending: boolean;
  chargeCents: number;
  prescriptions: Array<{ name: string; quantity: string | null; daysSupply: number | null; refills: number | null; directions: string | null; clinicalDifference: string | null }>;
}
interface ReadyResponse {
  plans: ReadyPlan[];
  /** RP-02: accepted plans held until a provider clears the health check (it is a question in Plan Questions). */
  held: Array<{ planId: string; patientId: string; patientName: string }>;
}

const STATUS = {
  ready: { text: 'Ready', chip: 'bg-mint text-forest' },
  needs_attention: { text: 'Needs attention', chip: 'bg-risk-yellow-soft text-risk-yellow' },
  waiting_in_dosespot: { text: 'Waiting in DoseSpot', chip: 'bg-[#efeee9] text-[#4d6072]' },
  prescribed: { text: 'Prescribed', chip: 'bg-forest text-white' },
} as const;

const WAIT_TEXT = { green: 'text-forest', amber: 'text-risk-yellow', red: 'text-alert' } as const;
const WAIT_CHIP = { green: 'bg-mint text-forest', amber: 'bg-risk-yellow-soft text-risk-yellow', red: 'bg-alert-soft text-alert' } as const;
const CONTACT = { text: 'Text', call: 'Call me', visit: '10-min visit' } as Record<string, string>;
const waited = (m: number) => (m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${Math.floor(m / 1440)}d`);

const chip = 'inline-flex items-center rounded-[5px] px-2 py-[3px] text-[11.5px]';
const label = 'text-[11px] font-semibold uppercase tracking-[0.14em] text-navy';

function Initials({ text }: { text: string }) {
  return <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#e3efe9] font-display text-[12px] font-semibold text-navy">{text}</span>;
}

export function PlanQuestionsPage() {
  const [params, setParams] = useSearchParams();
  const wanted = params.get('tab');
  const signQueue = useSignQueue();
  const signVisible = Boolean(signQueue.data?.visible);
  const tab = wanted === 'ready' ? 'ready' : wanted === 'sign' && signVisible ? 'sign' : 'questions';
  const queue = useQuery({ queryKey: ['plan-questions'], queryFn: () => api.get<{ questions: Row[] }>('/plan-questions'), refetchInterval: 60_000 });
  const ready = useQuery({ queryKey: ['plan-questions-ready'], queryFn: () => api.get<ReadyResponse>('/plan-questions/ready') });

  // S-23: the Sign tab is for prescribers only.
  const tabs: Array<{ key: 'ready' | 'questions' | 'sign'; text: string; count: number | undefined }> = [
    { key: 'ready', text: 'Ready to send', count: ready.data?.plans.length },
    { key: 'questions', text: 'Plan Questions', count: queue.data?.questions.length },
    ...(signVisible ? [{ key: 'sign' as const, text: 'Sign', count: signQueue.data?.rows.length }] : []),
  ];

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        title="Ready to Prescribe"
        subtitle={tab === 'ready' ? 'Plans your patients accepted. Send the prescriptions in DoseSpot.' : 'Patients who asked before accepting are your warmest plans. Answer fast.'}
      />
      <div className="mb-5 inline-flex gap-1 rounded-[12px] bg-[#ecebe5] p-1" role="tablist" aria-label="Ready to Prescribe">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setParams(t.key === 'questions' ? {} : { tab: t.key }, { replace: true })}
            className={`flex h-[42px] items-center gap-2 rounded-[9px] px-4 text-[15px] ${tab === t.key ? 'bg-white font-semibold text-navy shadow-sm' : 'text-[#4d6072] hover:text-navy'}`}
          >
            {t.text}
            {t.count != null && <span className="grid h-[22px] min-w-[22px] place-items-center rounded-full bg-forest px-1.5 text-[11.5px] font-semibold text-white">{t.count}</span>}
          </button>
        ))}
      </div>
      {tab === 'ready' ? <ReadyToSend query={ready} onQuestions={() => setParams({}, { replace: true })} /> : tab === 'sign' ? <SignQueue /> : <Questions query={queue} />}
    </div>
  );
}

/** Board 6: accepted plans on the left, the selected plan's prescriptions, checks and charge on the right. */
function ReadyToSend({ query, onQuestions }: { query: ReturnType<typeof useQuery<ReadyResponse>>; onQuestions: () => void }) {
  const [selected, setSelected] = useState<string | null>(null);
  const plans = query.data?.plans ?? [];
  const held = query.data?.held ?? [];
  useEffect(() => {
    if ((!selected || !plans.some((p) => p.planId === selected)) && plans[0]) setSelected(plans[0].planId);
  }, [plans, selected]);
  if (query.isLoading) return <LoadingRows rows={4} />;
  if (query.error) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  const plan = plans.find((p) => p.planId === selected) ?? null;
  return (
    <>
      {held.length > 0 && (
        <p className="mb-3 rounded-xl border border-[#ecd9a0] bg-risk-yellow-soft px-4 py-3 text-[14px] text-risk-yellow" role="status" data-testid="held-plans">
          {held.length === 1 ? `${held[0]!.patientName}'s plan is` : `${held.length} plans are`} held until the health check is reviewed.{' '}
          <button type="button" className="font-semibold underline" onClick={onQuestions}>
            Review it in Plan Questions
          </button>
        </p>
      )}
      {!plans.length ? (
        <Card><EmptyState title="No accepted plans waiting" body="When a patient accepts their plan, it waits here to be prescribed." /></Card>
      ) : (
        <div className="grid items-start gap-5 lg:grid-cols-[400px_minmax(0,1fr)]">
          <Card padded={false}>
            <div className="border-b border-hairline bg-[#f3f2ed] px-4 py-3"><span className={label}>Accepted plans</span></div>
            <ul className="divide-y divide-hairline" data-testid="ready-to-send">
              {plans.map((p) => (
                <li key={p.planId}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(p.planId);
                      bringDetailIntoView();
                    }}
                    aria-pressed={selected === p.planId}
                    className={`flex w-full gap-3 border-l-[3px] px-4 py-4 text-left ${selected === p.planId ? 'border-forest bg-mint/50' : 'border-transparent hover:bg-mint/25'}`}
                  >
                    <Initials text={p.initials} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="font-display text-[16px] font-semibold text-navy">{p.patientName}</span>
                        <span className="shrink-0 text-[13.5px] font-semibold text-navy">{p.monthly}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-[13px] text-[#4d6072]">{p.items.join(', ')}</span>
                      <span className={`${chip} mt-1.5 font-semibold ${STATUS[p.status].chip}`} data-testid="ready-status">{STATUS[p.status].text}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          {plan && <div data-detail className="min-w-0 scroll-mt-4 max-lg:min-h-[85vh]"><ReadyDetail plan={plan} /></div>}
        </div>
      )}
    </>
  );
}

/**
 * Below 1024px the detail sits under the list, so a tap on a row has to take you to it. The wrapper keeps a
 * screen's height while the new detail loads, or the scroll stops short against a page that has not grown yet.
 */
function bringDetailIntoView() {
  if (window.matchMedia('(min-width: 1024px)').matches) return;
  requestAnimationFrame(() => document.querySelector('[data-detail]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
}

function ReadyDetail({ plan }: { plan: ReadyPlan }) {
  const accepted = plan.acceptedAt
    ? new Date(plan.acceptedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;
  const doseSpotLabels: Record<string, string> = {
    'RP-11': 'No controlled items, or EPCS set up',
    'RP-12': 'You and the clinic are active in DoseSpot',
    'RP-13': 'Pharmacy matched in DoseSpot',
    'RP-14': 'Quantity, days supply and refills set',
  };
  return (
    <Card>
      <div data-testid="ready-detail">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={label}>Prescribe in DoseSpot</p>
            <h2 className="mt-1 font-display text-[26px] font-semibold leading-tight text-navy">{plan.patientName}</h2>
            <p className="mt-1 text-[14px] text-[#5b6b7c]">
              {[accepted && `Accepted ${accepted}`, `Plan v${plan.version}`, plan.signedBy && `signed by ${plan.signedBy}`].filter(Boolean).join(' · ')}
            </p>
          </div>
          <span className={`${chip} font-semibold ${STATUS[plan.status].chip}`}>{STATUS[plan.status].text}</span>
        </div>
        {plan.consentUpdatePending && (
          <p className="mt-3 rounded-[10px] border border-[#ecd9a0] bg-risk-yellow-soft px-3 py-2 text-[13.5px] text-risk-yellow" data-testid="consent-update-pending">
            Your clinic's consent changed in a way counsel marked material. {plan.patientName.split(' ')[0]} has been asked to accept it again in the app.
          </p>
        )}

        <div className="mt-5 overflow-x-auto rounded-[12px] border border-hairline">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline bg-[#f3f2ed] px-4 py-2.5">
            <span className="text-[13.5px] font-semibold text-navy">Prescriptions MCOS will queue in DoseSpot</span>
            <span className="text-[12.5px] text-[#5b6b7c]">Pharmacy: chosen in DoseSpot</span>
          </div>
          <table className="w-full min-w-[560px] text-left text-[13.5px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.12em] text-[#5b6b7c]">
                <th className="px-4 py-2 font-semibold">Medication</th>
                <th className="px-2 py-2 font-semibold">Qty</th>
                <th className="px-2 py-2 font-semibold">Days</th>
                <th className="px-2 py-2 font-semibold">Refills</th>
                <th className="px-4 py-2 font-semibold">Directions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {plan.prescriptions.map((rx) => (
                <tr key={rx.name} className="align-top">
                  <td className="px-4 py-3">
                    <span className="block font-display text-[15px] font-semibold text-navy">{rx.name}</span>
                    {rx.clinicalDifference ? (
                      <span className="block text-[12px] text-[#5b6b7c]">Note to pharmacist: clinical difference ({rx.clinicalDifference})</span>
                    ) : (
                      <span className="block text-[12px] text-[#5b6b7c]">Compounded</span>
                    )}
                  </td>
                  <td className="px-2 py-3 text-navy">{rx.quantity ?? <span className="text-[#7a8591]">In DoseSpot</span>}</td>
                  <td className="px-2 py-3 text-navy">{rx.daysSupply ?? <span className="text-[#7a8591]">-</span>}</td>
                  <td className="px-2 py-3 text-navy">{rx.refills ?? <span className="text-[#7a8591]">-</span>}</td>
                  <td className="px-4 py-3 text-navy">{rx.directions ?? <span className="text-[#7a8591]">Set in DoseSpot</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          <section className="rounded-[12px] border border-hairline p-4" data-testid="ready-checks">
            <h3 className="text-[13.5px] font-semibold text-navy">Checks before sending</h3>
            <ul className="mt-2 grid gap-x-4 gap-y-1.5 text-[13px] sm:grid-cols-2">
              {plan.checks.map((c) => (
                <li key={c.id} className="flex items-start gap-2" data-check={c.id}>
                  <CheckDot state={c.ok ? 'ok' : c.blocking ? 'fail' : 'warn'} />
                  <span className={c.ok ? 'text-[#2c3e4f]' : 'font-semibold text-navy'}>
                    <span className="sr-only">{c.ok ? 'Passed' : c.blocking ? 'Needs fixing' : 'Warning'}: </span>
                    {c.ok ? CHECK_PASSED[c.id] ?? c.message : c.message}
                  </span>
                </li>
              ))}
              {plan.dosespot.map((d) => (
                <li key={d.id} className="flex items-start gap-2 text-[#7a8591]" data-check={d.id}>
                  <CheckDot state="waiting" />
                  <span>
                    <span className="sr-only">Waiting for DoseSpot: </span>
                    {doseSpotLabels[d.id] ?? d.message}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[12px] text-[#7a8591]">Grey checks run once DoseSpot is connected.</p>
          </section>
          <section className="rounded-[12px] bg-[#1d2b3a] p-4 text-white">
            <p className="text-[13px] text-white/80">Charge in your billing system</p>
            <p className="mt-1 font-display text-[30px] font-semibold">${(plan.chargeCents / 100).toFixed(2)}</p>
            <p className="mt-1 text-[13px] leading-snug text-white/80">Monthly, once the prescription is sent. MCOS never charges the patient.</p>
          </section>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-hairline pt-4">
          <Button disabled title="DoseSpot is not connected yet">Send in DoseSpot</Button>
          <span className="text-[13px] text-[#4d6072]">
            DoseSpot is not connected yet. Until then, prescribe this accepted plan outside MCOS.{' '}
            <Link to={`/patients/${plan.patientId}/workspace`} className="font-semibold text-forest underline">Open the plan</Link>
          </span>
        </div>
      </div>
    </Card>
  );
}

function CheckDot({ state }: { state: 'ok' | 'fail' | 'warn' | 'waiting' }) {
  const bg = state === 'ok' ? 'bg-forest' : state === 'fail' ? 'bg-alert' : state === 'warn' ? 'bg-risk-yellow' : 'bg-[#c9cfd4]';
  return (
    <span aria-hidden="true" className={`mt-[2px] grid h-4 w-4 shrink-0 place-items-center rounded-full text-white ${bg}`}>
      {state === 'ok' ? (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"><path d="M5 12.5 10 17l9-10" /></svg>
      ) : (
        <span className="text-[10px] font-bold leading-none">{state === 'waiting' ? '' : '!'}</span>
      )}
    </span>
  );
}

/** What a passed check says (board 6 wording). */
const CHECK_PASSED: Record<string, string> = {
  'RP-01': 'Accepted this version',
  'RP-02': 'Health check clear',
  'RP-03': 'Every item prescribable',
  'RP-04': 'Clinical difference note fits',
  'RP-05': 'Ship-to address complete',
  'RP-06': "Licensed in the patient's state",
  'RP-07': 'Every item priced',
  'RP-08': 'Phone on file',
  'RP-09': 'Pharmacy ships to this state',
  'RP-10': 'Signature matches the plan',
};

function Questions({ query }: { query: ReturnType<typeof useQuery<{ questions: Row[] }>> }) {
  const [selected, setSelected] = useState<string | null>(null);
  const rows = query.data?.questions ?? [];
  useEffect(() => {
    if ((!selected || !rows.some((r) => r.id === selected)) && rows[0]) setSelected(rows[0].id);
  }, [rows, selected]);

  if (query.isLoading) return <LoadingRows rows={4} />;
  if (query.error) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  if (!rows.length) {
    return <Card><EmptyState title="No open questions" body="When a patient asks about their plan and the instant answer does not cover it, the question waits here." /></Card>;
  }
  return (
    <div className="grid items-start gap-5 lg:grid-cols-[400px_minmax(0,1fr)]">
      <Card padded={false}>
        <div className="border-b border-hairline bg-[#f3f2ed] px-4 py-3"><span className={label}>Longest waiting first</span></div>
        <ul className="divide-y divide-hairline" data-testid="question-queue">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => {
                  setSelected(row.id);
                  bringDetailIntoView();
                }}
                aria-pressed={selected === row.id}
                className={`flex w-full gap-3 border-l-[3px] px-4 py-4 text-left ${selected === row.id ? 'border-forest bg-mint/50' : 'border-transparent hover:bg-mint/25'}`}
              >
                <Initials text={row.initials} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="font-display text-[16px] font-semibold text-navy">{row.patientName}</span>
                    <span className={`shrink-0 text-[12px] font-semibold ${WAIT_TEXT[row.wait]}`}>{waited(row.waitMinutes)}</span>
                  </span>
                  <span className="mt-1.5 block text-[14px] text-navy">{row.question}</span>
                  <span className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className={`${chip} bg-mint font-semibold text-forest`}>{row.topicLabel}</span>
                    {row.prescriber && <span className={`${chip} bg-[#efeee9] text-[#4d6072]`}>{row.prescriber}</span>}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Card>
      {selected && (
        <div data-detail className="min-w-0 scroll-mt-4 max-lg:min-h-[85vh]">
          <QuestionDetail key={selected} id={selected} level={rows.find((r) => r.id === selected)?.wait ?? 'green'} />
        </div>
      )}
    </div>
  );
}

function QuestionDetail({ id, level }: { id: string; level: Row['wait'] }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detail = useQuery({ queryKey: ['plan-question', id], queryFn: () => api.get<Detail>(`/plan-questions/${id}`) });
  const [text, setText] = useState('');
  const [checked, setChecked] = useState<{ phrase: string; why: string }[] | null>(null);
  useEffect(() => {
    if (detail.data) setText(detail.data.suggestedAnswer ?? '');
  }, [detail.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // PG-004, live: the same check the server runs on send, a moment after typing stops.
  useEffect(() => {
    if (!text.trim()) return setChecked(null);
    const timer = window.setTimeout(() => {
      api.post<{ violations: { phrase: string; why: string }[] }>('/plan-questions/check', { text }).then((r) => setChecked(r.violations)).catch(() => setChecked(null));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [text]);

  const answer = useMutation({
    mutationFn: (channel: 'in_app' | 'call' | 'revised_plan') => api.post<Detail>(`/plan-questions/${id}/answer`, { channel, text }),
    onSuccess: (_d, channel) => {
      void qc.invalidateQueries({ queryKey: ['plan-questions'] });
      // Answering a health check question clears its hold on Ready to send (RP-02).
      void qc.invalidateQueries({ queryKey: ['plan-questions-ready'] });
      void qc.invalidateQueries({ queryKey: ['nav-counts'] });
      if (channel === 'revised_plan' && detail.data) navigate(`/patients/${detail.data.patientId}/workspace`);
    },
  });

  if (detail.isLoading) return <Card><LoadingRows rows={4} /></Card>;
  if (detail.error || !detail.data) return <ErrorState error={detail.error} retry={() => void detail.refetch()} />;
  const d = detail.data;
  const blocked = (checked?.length ?? 0) > 0;
  return (
    <Card>
      <div data-testid="question-detail">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-[26px] font-semibold leading-tight text-navy">{d.patientName}</h2>
            <p className="mt-1 text-[14px] text-[#5b6b7c]">{d.planLine}</p>
          </div>
          <div className="flex gap-2">
            <span className={`${chip} font-semibold ${WAIT_CHIP[level]}`}>Waiting {waited(d.waitMinutes)}</span>
            {d.prescriber && <span className={`${chip} bg-[#efeee9] font-semibold text-navy`}>{d.prescriber}</span>}
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <section className="rounded-[12px] bg-[#f3f2ed] p-4">
            <h3 className={label}>They asked</h3>
            <span className={`${chip} mt-2 bg-mint font-semibold text-forest`}>{d.topicLabel}</span>
            <p className="mt-2 font-display text-[19px] font-semibold leading-snug text-navy">{d.question}</p>
            {d.yourWords && <p className="mt-2 border-l-2 border-[#d8d3c8] pl-3 text-[14.5px] italic text-[#4d6072]">"{d.yourWords}"</p>}
            {d.contactPref && (
              <p className="mt-3 text-[13px] text-[#5b6b7c]">
                Prefers: {CONTACT[d.contactPref] ?? d.contactPref}
                {d.contactPref === 'call' && d.patientPhone ? `, ${d.patientPhone}` : ''}
              </p>
            )}
          </section>
          <section className="rounded-[12px] border border-hairline p-4">
            <h3 className={label}>What they did on the plan</h3>
            {d.activity.length ? (
              <ul className="mt-2 list-disc space-y-1.5 pl-5 text-[14px] text-navy marker:text-navy" data-testid="question-activity">
                {d.activity.map((line) => <li key={line}>{line}</li>)}
              </ul>
            ) : (
              <p className="mt-2 text-[14px] text-[#5b6b7c]">Nothing recorded yet.</p>
            )}
          </section>
        </div>

        <div className="mt-5 flex flex-wrap items-baseline justify-between gap-2">
          <label htmlFor="answer-text" className={label}>Suggested answer</label>
          {d.suggestedAnswer && <span className="text-[12.5px] font-semibold text-navy">Drafted from the plan and your clinic's approved answers</span>}
        </div>
        <textarea id="answer-text" className={`${inputClass} mt-2 min-h-[150px] text-[15px] leading-relaxed`} value={text} onChange={(e) => setText(e.target.value)} />
        {checked && (
          <p className={`mt-2 flex items-center gap-1.5 text-[13px] ${blocked ? 'text-alert' : 'text-[#4d6072]'}`} data-testid="timeline-check" role="status">
            {blocked ? (
              <>Change "{checked[0]!.phrase}": {checked[0]!.why}</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-forest" aria-hidden="true"><path d="M5 12.5 10 17l9-10" /></svg>
                Passed the timeline check: no set length, no stop dates, no promises. Edit before sending.
              </>
            )}
          </p>
        )}
        {answer.error && <p className="mt-2 text-sm text-red-600" role="alert">{answer.error instanceof Error ? answer.error.message : 'Could not send'}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button disabled={!text.trim() || blocked} loading={answer.isPending} onClick={() => answer.mutate('in_app')}>Send answer</Button>
          <Button variant="secondary" disabled={!text.trim() || blocked} loading={answer.isPending} onClick={() => answer.mutate('call')}>Offer a call</Button>
          <Button variant="secondary" disabled={!text.trim() || blocked} loading={answer.isPending} onClick={() => answer.mutate('revised_plan')}>Send revised plan</Button>
          <InfoHint label="Where the answer goes">The answer shows on the patient's plan in their app. A text alert starts once the Twilio agreement is signed.</InfoHint>
        </div>
      </div>
    </Card>
  );
}

interface Texts {
  priceIncludes: string | null;
  shippingText: string | null;
  onboardingText: string | null;
  waitAmberMinutes: number;
  waitRedMinutes: number;
  messageReplyTimeText: string | null;
  careHoursText: string | null;
  messageAutocloseDays: number;
  alertQuietStart: string;
  alertQuietEnd: string;
  alertThrottleMin: number;
  retentionYears: number;
}

/**
 * The clinic's own words (spec Merge Fields tab: "Clinic settings are written once by each clinic in Settings").
 * Instant answers that need one stay with the care team until it is filled; the reply time and care hours show in
 * the patient's Messages (MS-006, MS-009), and conversations waiting on the patient close after the set days (MS-008).
 */
export function ClinicWordingCard({ className = '' }: { className?: string }) {
  const qc = useQueryClient();
  const texts = useQuery({ queryKey: ['plan-question-texts'], queryFn: () => api.get<Texts>('/plan-questions/texts') });
  const [form, setForm] = useState<Texts | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (texts.data) setForm(texts.data);
  }, [texts.data]);
  const save = useMutation({
    mutationFn: () => api.put<Texts>('/plan-questions/texts', form),
    onSuccess: (data) => {
      qc.setQueryData(['plan-question-texts'], data);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });
  if (!form) return null;
  const set = (patch: Partial<Texts>) => setForm({ ...form, ...patch });
  return (
    <Card title="Patient wording" subtitle="Your clinic's own words in instant answers and Messages. An instant answer that needs one of these goes to your care team until it is filled in." className={className}>
      <div className="grid gap-3 lg:grid-cols-2" data-testid="answer-texts">
        <Field label="What the monthly price includes" hint="For example: your medication, supplies and shipping, plus your monthly check-ins">
          <input className={inputClass} value={form.priceIncludes ?? ''} onChange={(e) => set({ priceIncludes: e.target.value })} />
        </Field>
        <Field label="Shipping" hint="For example: Most orders ship within a few business days, and we will let you know when it ships.">
          <input className={inputClass} value={form.shippingText ?? ''} onChange={(e) => set({ shippingText: e.target.value })} />
        </Field>
        <Field label="Getting started" hint="For example: Your clinician will walk you through your first dose by video.">
          <input className={inputClass} value={form.onboardingText ?? ''} onChange={(e) => set({ onboardingText: e.target.value })} />
        </Field>
        <Field label="Message reply time" hint="Shown in the patient's Messages. For example: within one business day">
          <input className={inputClass} value={form.messageReplyTimeText ?? ''} onChange={(e) => set({ messageReplyTimeText: e.target.value })} />
        </Field>
        <Field label="Care hours" hint="For example: Mon to Fri, 8 AM to 5 PM. Empty reads as business hours.">
          <input className={inputClass} value={form.careHoursText ?? ''} onChange={(e) => set({ careHoursText: e.target.value })} />
        </Field>
        <Field label="Close conversations waiting on the patient after, days" hint="The patient can start a new message any time.">
          <input className={inputClass} inputMode="numeric" value={form.messageAutocloseDays} onChange={(e) => set({ messageAutocloseDays: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Text alerts quiet from" hint="In the patient's time zone. An alert due in quiet hours waits until they end.">
          <input type="time" className={inputClass} value={form.alertQuietStart} onChange={(e) => set({ alertQuietStart: e.target.value })} />
        </Field>
        <Field label="Text alerts quiet until">
          <input type="time" className={inputClass} value={form.alertQuietEnd} onChange={(e) => set({ alertQuietEnd: e.target.value })} />
        </Field>
        <Field label="At most one text alert per conversation every, minutes" hint="The link in the first alert still opens the conversation.">
          <input className={inputClass} inputMode="numeric" value={form.alertThrottleMin} onChange={(e) => set({ alertThrottleMin: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Keep messages and photos for, years" hint="Part of the patient record. 10 by default, at least 7. Nothing sent is deleted sooner.">
          <input className={inputClass} inputMode="numeric" value={form.retentionYears} onChange={(e) => set({ retentionYears: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Waiting turns amber after, minutes">
          <input className={inputClass} inputMode="numeric" value={form.waitAmberMinutes} onChange={(e) => set({ waitAmberMinutes: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Waiting turns red after, minutes">
          <input className={inputClass} inputMode="numeric" value={form.waitRedMinutes} onChange={(e) => set({ waitRedMinutes: Number(e.target.value) || 0 })} />
        </Field>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Button disabled={form.waitRedMinutes <= form.waitAmberMinutes || form.messageAutocloseDays < 1 || form.alertThrottleMin < 5 || form.retentionYears < 7 || !form.alertQuietStart || !form.alertQuietEnd} loading={save.isPending} onClick={() => save.mutate()}>Save wording</Button>
        {saved && <span className="text-sm text-emerald-700" role="status">Saved</span>}
        {save.error && <span className="text-sm text-red-600">{save.error instanceof Error ? save.error.message : 'Could not save.'}</span>}
      </div>
    </Card>
  );
}
